import { useCallback, useEffect, useMemo, useState } from "react";
import { getClientsByOrganizationAsync } from "../api/ClientApi";
import { Client } from "../models/ClientModels";
import { PagedResult } from "../models/PagedModels";
import { Constants } from "../constants/constants";

const DEFAULT_PAGE_SIZE = 20;

export interface ClientFilters {
  search?: string;
  isActive?: boolean;
}

interface UseClientsOptions {
  pageSize?: number;
}

interface UseClientsReturn {
  clients: Client[];
  pagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
  loading: boolean;
  error: string | null;
  filters: ClientFilters;
  setFilters: (filters: ClientFilters) => void;
  setPage: (page: number) => void;
  reload: () => Promise<void>;
  addLocal: (client: Client) => void;
  updateLocal: (client: Client) => void;
  removeLocal: (id: string) => void;
}

export const useClients = (
  organizationId: string | undefined,
  options: UseClientsOptions = {},
): UseClientsReturn => {
  const pageSize = options.pageSize ?? DEFAULT_PAGE_SIZE;
  const [page, setPage] = useState(1);
  const [filters, setFiltersState] = useState<ClientFilters>({});
  const [data, setData] = useState<PagedResult<Client> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";

  const load = useCallback(async () => {
    if (!organizationId || !token) {
      setData(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const res = await getClientsByOrganizationAsync(token, organizationId, {
        page,
        pageSize,
        search: filters.search?.trim() || undefined,
        isActive: filters.isActive,
      });
      if (res.success && res.result) {
        setData(res.result);
        setError(null);
      } else {
        setError(res.message || "Failed to load clients");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load clients");
    } finally {
      setLoading(false);
    }
  }, [organizationId, token, page, pageSize, filters.search, filters.isActive]);

  useEffect(() => {
    load();
  }, [load]);

  const setFilters = useCallback((next: ClientFilters) => {
    setPage(1);
    setFiltersState((prev) => ({ ...prev, ...next }));
  }, []);

  const reload = useCallback(async () => {
    setPage(1);
    await load();
  }, [load]);

  const addLocal = useCallback((client: Client) => {
    setData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        items: [client, ...prev.items],
        totalCount: prev.totalCount + 1,
      };
    });
  }, []);

  const updateLocal = useCallback((client: Client) => {
    setData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        items: prev.items.map((item) =>
          item.id === client.id ? client : item,
        ),
      };
    });
  }, []);

  const removeLocal = useCallback((id: string) => {
    setData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        items: prev.items.filter((item) => item.id !== id),
        totalCount: Math.max(0, prev.totalCount - 1),
      };
    });
  }, []);

  const clients = useMemo(() => data?.items ?? [], [data]);

  return {
    clients,
    pagination: {
      page: data?.page ?? page,
      pageSize: data?.pageSize ?? pageSize,
      totalCount: data?.totalCount ?? 0,
      totalPages: data?.totalPages ?? 1,
      hasNext: data?.hasNext ?? false,
      hasPrevious: data?.hasPrevious ?? false,
    },
    loading,
    error,
    filters,
    setFilters,
    setPage,
    reload,
    addLocal,
    updateLocal,
    removeLocal,
  };
};